<script setup lang="ts">
import { type Component, computed, inject, ref, watch, onUnmounted } from 'vue';
import { useStorage } from '@vueuse/core';
import DynamicForm from '@pkg/form-builder/web/components/DynamicForm.vue';
import { useDynamicForm } from '@pkg/form-builder/web/composables/useDynamicForm';
import type { FormFragment } from '@pkg/sanity-utils/types/forms';
import { CONFIRMATION_TYPE, FORM_ERROR, INPUT_TYPE } from '@pkg/sanity-utils/types/forms';
import { z } from '@pkg/form-builder/zod';
import type { ZodSchema } from '@pkg/form-builder/zod';
import { resolveConfirmationType } from '@pkg/sanity-utils/forms/resolveConfirmationType';
import { fieldToOptions, fieldToValidation, inputTypeToComponent, inputTypeToType } from './helpers/inputTypeMaps';
import type { FieldSchema, FormSchema } from '@pkg/form-builder/types';
import FormConfirmation from './FormConfirmation.vue';
import { logger } from '@pkg/logger';
import { useParseMarketing } from '@pkg/analytics-helper/src/composables/useParseMarketing';
import { metaPrefix } from '@pkg/config/misc/cmsFormSubmission';
import { ErrorMessage } from 'vee-validate';
import { getIso2Digit } from '@pkg/utilities/i18n';
import { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { formAnalyticsKey } from '../../../frontend/formAnalytics';

declare const window: {
  CookieInformation: any;
  open: (url: string, target: string) => void;
};

interface PayloadItem {
  value: {
    confirmationParams: {
      uuid?: string;
      url?: string;
    };
  };
}

const props = defineProps<{
  form: FormFragment;
  modularRenderer: Component;
  extraFields?: { [key: string]: string }; // Extra form data, useful for user-made selections outside the form
  extraMeta?: { [key: string]: string }; // Extra form meta, useful for meta captured in one-off locations, rather than on all forms
}>();

const route = useRoute();
const { locale } = useI18n();
const { $i18n } = useNuxtApp();
const t = $i18n.t;
const formAnalytics = inject(formAnalyticsKey, null);

const { public: { domain } } = useRuntimeConfig();
const { getReferrer, getCampaignCookie, getFacebookCampaignCookies } = useParseMarketing();

/**
 * Check if form is properly loaded with all required data
 */
const isFormLoaded = computed(() => {
  const isLoaded = Array.isArray(props?.form?.fields) && 
    props.form.fields.length > 0;
  
  if (!isLoaded) {
    logger.warn('[FormsBlock] Form not properly loaded:', {
      hasForm: !!props.form,
      hasFields: !!props.form?.fields,
      fieldsIsArray: Array.isArray(props.form?.fields),
      fieldsLength: props.form?.fields?.length
    });
  }
  
  return isLoaded;
});

/**
 * Convert CMS fields into a formSchema compatible with the form-builder package.
 */

const schema: FormSchema = (props.form?.fields || []).reduce((acc, field) => {
  const isNoteField = field.inputType === INPUT_TYPE.NOTE;

  return {
    ...acc,
    [field.fieldId.current]: {
      component: inputTypeToComponent(field.inputType),
      label: field.fieldLabel,
      type: inputTypeToType(field.inputType),
      validation: isNoteField ? undefined : fieldToValidation(field),
      options: fieldToOptions(field.options),
      required: field.required,
      description: field.description,
      placeholder: field.placeholder,
      defaultValue: undefined, // @todo
      noteText: field.noteText
    } satisfies FieldSchema,
  };
}, {});

const validationShape = Object.entries(schema).reduce<Record<string, ZodSchema>>((acc, [fieldKey, field]) => {
  if (field.validation) {
    acc[fieldKey] = field.validation;
  }
  return acc;
}, {});

const validationSchema = Object.keys(validationShape).length > 0
  ? z.object(validationShape)
  : undefined;

const { form } = useDynamicForm({
  formSchema: Object.keys(schema).length > 0 ? schema : {},
  validationSchema,
});

const sanitizeInput = (input: string) => {
  return input.replace(/[&<>"'/]/g, (char) => {
    switch (char) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#x27;';
      case '/': return '&#x2F;';
      default: 
      return char;
    }
  });
};

let amplitudeForm = ref();


function toSnakeCaseUpper(str: string): string {
  const afterDot = str.split('.').pop() || str;
  const snakeCase = afterDot.replace(/([a-z])([A-Z])/g, '$1_$2');
  return snakeCase.toUpperCase();
}

let formName: string | null = null;

watch(() => props.form, (newForm) => {
  formName = newForm.notifications?.[0]?._type ?? null;
  amplitudeForm = useStorage('amplitudeForm', {});
  if (newForm && formName) {
    formName = toSnakeCaseUpper(formName);
    amplitudeForm.value = {
      formName: formName ?? 'UNKNOWN_FORM',
      formId: newForm._id ? `CMS-${newForm._id}` : null,
    };
  }
}, { immediate: true });

onUnmounted(() => {
  amplitudeForm.value = null;
});

const turnstileToken = ref<string | null>(null);
const turnstileVerified = ref(false);

const handleTurnstileToken = (token: string | null) => {
  turnstileToken.value = token;
};

const handleTurnstileVerified = (verified: boolean) => {
  if (verified) {
    turnstileVerified.value = true;
  }
};

const onSubmit = form.handleSubmit(async (values, actions) => {
  // See: https://serversideup.net/post-put-and-patch-requests-with-nuxt-3/
  
  let formData = new FormData();

  let campaignEnrichment: { [key: string]: any } = {};
  const allowedKeys = ['email_address', 'phone_number', 'city_name'];

  Object.keys(values).forEach(key => {
    const sanitizedValue = typeof values[key] === 'string' ? sanitizeInput(values[key]) : values[key];
    if (allowedKeys.includes(key) && window.CookieInformation?.getConsentGivenFor('cookie_cat_marketing')) {
      campaignEnrichment[key as string] = sanitizedValue;
    }
    formData.append(key, sanitizedValue);
  });
  
  formData.append('sanityFormKey', props.form?._id || ''); // We need this in order to process form notifications
    
  try {
    // Always include Turnstile token for all form submissions
    if (turnstileToken.value) {
      formData.append('cloudflare_token', turnstileToken.value);
    }
    formData.append('country_code', getIso2Digit(locale.value as LOCALE_CODE) || '');
    formData.append('localization_code', locale.value || '');
    formData.append('opt_in_privacy_policy','true');

    formData.append('submissionRoute', `${domain}${route.fullPath}`);
    if (window?.CookieInformation?.getConsentGivenFor && typeof window.CookieInformation.getConsentGivenFor === 'function') {
      formData.append('cookie_consent', String(!!(window.CookieInformation?.getConsentGivenFor('cookie_cat_marketing'))));
    }

    getCampaignCookie(document).forEach((param) => formData.append(`${metaPrefix}${param.split('=')[0]}`, param.split('=')[1]));
    getFacebookCampaignCookies(document).forEach((param) => formData.append(`${param.split('=')[0]}`, param.split('=')[1]));
    getReferrer(document).forEach((param) => {
      const referal = param.match(/(?:[?&])referred_by=([^&]+)/);
      formData.append(`${metaPrefix}${param.split('=')[0]}`, referal ? referal[1] : '')
    });

    if (props.extraFields) {
      const extraFields = props.extraFields;
      Object.keys(extraFields).forEach(key => formData.append(key, extraFields[key]));
    }
    if (props.extraMeta) {
      const extraMeta = props.extraMeta;
      Object.keys(extraMeta).forEach(key => formData.append(`${metaPrefix}${key}`, extraMeta[key]));
    }
  } catch (error) {
    logger.error('[CmsFormsBlock] error', error);
  }

  logger.debug('[CmsFormsBlock] cms-form-submission', formData);
  console.log('[CmsFormsBlock] cms-form-submission', formData);

  try {
    const response = await $fetch<{ status: string; reason?: { message: string }; payload?: PayloadItem[] }>('/api/cms-form-submission', {
      method: 'POST',
      body: formData,
    });

    if(response.status === 'rejected') {
      throw new Error(response.reason?.message || t('zodI18n.validations.generic'));
    }
    
    logger.debug('[CmsFormsBlock] response', response);

    actions.resetForm();

    const confirmationType = resolveConfirmationType(props.form?.confirmation);
    const contentRedirectUrl = getContentRedirectUrl(
      response.payload || [],
      props.form?.confirmation,
      confirmationType,
    );

    if (contentRedirectUrl) {
      const url = getTemplate(response.payload || [], contentRedirectUrl);

      if (confirmationType === CONFIRMATION_TYPE.AUTOMATED_REDIRECT) {
        window.location.href = url;
        return;
      }

      if (confirmationType === CONFIRMATION_TYPE.REDIRECT) {
        hasSubmitted.value = true;
        window.open(url, '_blank');
        return;
      }
    }

    hasSubmitted.value = true;

  } catch (error: any) {
    formAnalytics?.trackFormError(FORM_ERROR.API_REJECTED);
    hasSubmitted.value = false;
    actions.setErrors({ generic: error['message'] || error['statusMessage'] || t('zodI18n.validations.generic') });
  }
},(errors) => {
  if (errors) {
    formAnalytics?.trackFormError(FORM_ERROR.CLIENT_VALIDATION);
  }
});

const hasSubmitted = ref(false);
const isSubmitting = computed(() => form.isSubmitting.value);

function getContentRedirectUrl(
  payload: PayloadItem[],
  confirmation?: FormFragment['confirmation'],
  confirmationType?: CONFIRMATION_TYPE,
): string | undefined {
  const resolvedType = confirmationType ?? resolveConfirmationType(confirmation);

  if (resolvedType === CONFIRMATION_TYPE.AUTOMATED_REDIRECT) {
    return payload.find(item => item.value?.confirmationParams?.url)?.value.confirmationParams?.url;
  }

  if (resolvedType === CONFIRMATION_TYPE.REDIRECT && confirmation && 'contentRedirectUrl' in confirmation) {
    return confirmation.contentRedirectUrl ?? undefined;
  }

  return undefined;
}

const getTemplate = (payload: PayloadItem[], templateString: string) => {
  const hasConfirmationParams = payload.find(item => item.value.confirmationParams !== null)?.value.confirmationParams;
  const regex = /{{(\w+)}}/g;
  return templateString.replace(regex, (match, key) => {
    // Navigate through the object to find the nested key if exists
    let value = key.split('.').reduce((acc: any, part: any) => {
      return acc && acc[part] ? acc[part] : null;
    }, hasConfirmationParams);

    // If the key exists in the replacements object, return its value. Otherwise, return an empty string.
    return value !== null ? value : '';
  });
};

</script>

<template>
  <div v-if="isFormLoaded" class="wrapper wrapper--content">
    <DynamicForm
      v-show="!hasSubmitted"
      @submit="onSubmit"
      @turnstile-token="handleTurnstileToken"
      @turnstile-verified="handleTurnstileVerified"
    >
      <template #endOfForm>
        <div class="flex justify-center tablet-wide:justify-start">
          <UiButton type="submit" :disabled="!turnstileVerified">
            {{ props.form?.submitButtonText }}
          </UiButton>
        </div>
        <div class="flex justify-start">
          <ErrorMessage class="text-body--xsmall font-medium text-[color:var(--input-error-border)]" name="generic"/>
        </div>
      </template>
    </DynamicForm>
    <div v-show="hasSubmitted">
      <FormConfirmation :confirmation="props.form?.confirmation" :modular-renderer="props.modularRenderer"/>
    </div>
  </div>
</template>
